import { isMobile } from '@utilities/theme';
import { normalizeFilterValue } from '@utilities/filter-labels';

/**
 * PriceRange - Custom element for handling price range filter inputs
 *
 * Manages min/max price input validation and ensures logical price ranges
 * Automatically adjusts values to prevent invalid range selections
 */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

/**
 * FacetRemove - Simplified component for handling filter removal links
 *
 * Intercepts click events on filter removal links and dispatches custom events
 * for FilterDrawer to handle URL updates and state management
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      // Dispatch custom event for FilterDrawer to handle removal
      this.dispatchEvent(new CustomEvent('facet-remove:clicked', {
        detail: { originalEvent: event },
        bubbles: true
      }));
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 * FeaturedFilters - Component for managing sticky filter buttons and drawer coordination
 *
 * This component handles the featured filter buttons that appear at the top of collection/search pages
 * and provides sticky behavior on mobile. It coordinates with the FilterDrawer component to maintain
 * consistent filter state across different UI elements.
 *
 * Key Features:
 * - Sticky filter behavior on mobile using Intersection Observer
 * - Bidirectional synchronization with FilterDrawer component
 * - URL state initialization and maintenance
 * - Legacy facets.js compatibility for server-side filtering
 * - Responsive header height adjustment
 *
 * Behavior:
 * - Detects when FilterDrawer system is active and delegates filter handling
 * - Falls back to legacy form-based filtering for server-side implementations
 * - Automatically syncs button states with current URL parameters
 * - Provides sticky positioning when scrolled past original position
 */
class FeaturedFilters extends HTMLElement {
  constructor() {
    super();
  }

  allowDebug() {
    return this.classList.contains('debug');
  }

  connectedCallback() {
    this.init();
    this.initStickyFilter();
  }

  /**
   * Debug logging utility - only logs when debug mode is enabled
   * @param {string} message - The debug message
   * @param {...any} args - Additional arguments to log
   * @returns {void}
   * @private
   */
  debugLog(message, ...args) {
    if(!this.allowDebug()) return;
    console.log(`[STICKY FILTER DEBUG] ${message}`, ...args);
  }

  init() {
    this.initElements();
    this.initEventListeners();
    this.initializeFromUrl();
  }

  initElements() {
    this.options = Array.from(this.querySelectorAll('input[type="checkbox"]'));
    this.optionLinks = Array.from(this.querySelectorAll('label a'));
    // Find the filters form in the document (now a regular form element)
    this.filtersForm = document.querySelector('form.facet-filters-form');
    // Find the filter drawer
    this.filterDrawer = document.querySelector('filter-drawer');
  }

  initEventListeners() {
    this.addEventListener("change", (event) => {
      if (event.target.matches('input[type="checkbox"]')) {
        this.handleCheckboxChange(event.target);
      }
    });
  }

  /**
   * Move filter opener to header on mobile when product grid reaches viewport top
   * This eliminates gap issues by making the filter opener part of the header layout
   * @returns {void}
   * @private
   */
  initStickyFilter() {
    const filterOpener = this.querySelector('.slide-out-drawer-opener.filter-opener');

    if (!filterOpener) {
      return;
    }

    // Find elements needed for positioning
    const siteHeader = document.getElementById('site-header');
    const productGrid = document.querySelector('product-grid[data-main-product-grid="true"]')
      || document.querySelector('[data-product-grid-container]');
    const headerFilterContainer = siteHeader?.querySelector('.header-filter-container');

    if (!siteHeader || !productGrid || !headerFilterContainer) {
      return;
    }

    // Create a header clone so we can toggle visibility without layout shifts
    let headerClone = headerFilterContainer.querySelector('.filter-opener--header-clone');
    if (!headerClone) {
      headerClone = filterOpener.cloneNode(true);
      headerClone.classList.add('filter-opener--header-clone');
      headerClone.removeAttribute('id');
      headerClone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      headerFilterContainer.appendChild(headerClone);
    }

    const setAriaHidden = (element, hidden) => {
      if (!element) return;
      element.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      element.querySelectorAll('button').forEach((button) => {
        if (hidden) {
          button.setAttribute('tabindex', '-1');
        } else {
          button.removeAttribute('tabindex');
        }
      });
    };

    let isHeaderVisible = false;
    const updateVisibility = (showHeader) => {
      if (showHeader === isHeaderVisible) return;
      headerClone.classList.toggle('filter-opener--hidden', !showHeader);
      setAriaHidden(headerClone, !showHeader);
      headerFilterContainer.classList.toggle('header-filter-container--active', showHeader);
      isHeaderVisible = showHeader;
    };

    // Ensure header clone starts hidden until threshold is hit
    updateVisibility(false);

    let cachedHeaderHeight = 0;
    let observer = null;
    let sentinel = productGrid.querySelector('.filter-opener-sentinel');

    const ensureSentinel = () => {
      if (sentinel) return;
      sentinel = document.createElement('div');
      sentinel.classList.add('filter-opener-sentinel');
      sentinel.style.display = 'block';
      sentinel.style.height = '1px';
      sentinel.style.marginTop = '-1px';
      sentinel.style.pointerEvents = 'none';
      productGrid.prepend(sentinel);
    };

    const setupObserver = () => {
      if (!sentinel) return;
      if (observer) observer.disconnect();
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!isMobile()) {
              updateVisibility(false);
              return;
            }

            // When sentinel scrolls past the header, it leaves the adjusted root
            const shouldShowHeader = !entry.isIntersecting;
            updateVisibility(shouldShowHeader);
          });
        },
        {
          rootMargin: `-${cachedHeaderHeight}px 0px 0px 0px`,
          threshold: 0
        }
      );
      observer.observe(sentinel);
    };
    const readHeaderHeight = () => {
      const themeHeaderHeight = window.theme?.header?.height;
      if (typeof themeHeaderHeight === 'number') {
        return themeHeaderHeight;
      }
      const headerHeight = getComputedStyle(document.body).getPropertyValue('--header-height');
      return headerHeight ? parseInt(headerHeight, 10) || 0 : 0;
    };

    const handleHeaderHeightChange = (event) => {
      const nextHeight = event?.detail?.height;
      if (typeof nextHeight === 'number') {
        cachedHeaderHeight = nextHeight;
      } else {
        cachedHeaderHeight = readHeaderHeight();
      }
      setupObserver();
    };

    ensureSentinel();
    cachedHeaderHeight = readHeaderHeight();
    setupObserver();
    document.addEventListener('header-height:change', handleHeaderHeightChange);
  }

  /**
   * Handle checkbox state changes for filter buttons
   * Delegates to FilterDrawer system when active, falls back to legacy form handling
   * @param {HTMLInputElement} option - The checkbox that was changed
   */
  async handleCheckboxChange(option) {
    // Check if filter drawer system is active and should handle this instead
    if (this.filterDrawer && this.filterDrawer.classList.contains('filter-drawer--initialized')) {
      this.debugLog('FeaturedFilters: FilterDrawer system is active, ensuring drawer is ready for filter sync');

      // If the filter drawer hasn't rendered its content yet, we need to trigger it
      if (!this.filterDrawer.filtersRendered) {
        this.debugLog('FeaturedFilters: Filter drawer not rendered yet, triggering render');
        await this.ensureFilterDrawerRendered();
      }

      // Now let the filter drawer handle the change by dispatching to it directly
      this.handleFilterDrawerChange(option);
      return;
    }

    // Legacy behavior when filter drawer is not active
    const type = option.dataset.type;
    const values = option.dataset.value.split(',');
    let triggerChange = false;
    if (!this.filtersForm) {
      console.error("No filters form found");
      return;
    }
    // Since filtersForm is now the actual form element, use it directly
    const form = this.filtersForm;
    values.forEach((value) => {
      const targetEl = form.querySelector(`input[name="${type}"][data-value="${value}"]`);
      if (targetEl) {
        targetEl.checked = option.checked;
        triggerChange = true;
        // Dispatch both input and change events
        targetEl.dispatchEvent(new Event('input', { bubbles: true }));
        targetEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (triggerChange) {
      // Dispatch events on the form itself
      form.dispatchEvent(new Event('input', { bubbles: true }));
      form.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Ensure the filter drawer has rendered its content
   * @returns {Promise<void>}
   */
  async ensureFilterDrawerRendered() {
    if (!this.filterDrawer) {
      console.warn('FeaturedFilters: No filter drawer found');
      return;
    }

    // If already rendered, nothing to do
    if (this.filterDrawer.filtersRendered) {
      return;
    }

    // Check if the drawer should auto-load data
    if (!this.filterDrawer.shouldAutoLoad || !this.filterDrawer.shouldAutoLoad()) {
      console.warn('FeaturedFilters: Filter drawer should not auto-load');
      return;
    }

    // Show loading state
    if (this.filterDrawer.showLoadingState) {
      this.filterDrawer.showLoadingState();
    }

    try {
      // Load filter data
      if (this.filterDrawer.loadFilterData) {
        await this.filterDrawer.loadFilterData();
      }

      // Render filters
      if (this.filterDrawer.state && this.filterDrawer.state.data && this.filterDrawer.state.data.filters) {
        if (this.filterDrawer.renderFilters) {
          this.filterDrawer.renderFilters(this.filterDrawer.state.data.filters);
        }
      } else {
        if (this.filterDrawer.renderEmptyFilters) {
          this.filterDrawer.renderEmptyFilters();
        }
      }
    } catch (error) {
      console.error('FeaturedFilters: Failed to render filter drawer:', error);
      if (this.filterDrawer.showErrorState) {
        this.filterDrawer.showErrorState('Failed to load filters');
      }
    }
  }

  /**
   * Handle filter change when filter drawer is active
   * @param {HTMLInputElement} option - The checkbox that was changed
   */
  handleFilterDrawerChange(option) {
    const filterName = option.dataset.type;
    const filterValue = option.dataset.value;
    const action = option.checked ? 'add' : 'remove';

    this.debugLog('FeaturedFilters: Handling filter drawer change', { filterName, filterValue, action });

    // Update the filter drawer's internal state directly
    if (this.filterDrawer && this.filterDrawer.handleFilterChange) {
      this.filterDrawer.handleFilterChange(filterName, filterValue, action);
    } else {
      // Fallback: dispatch the filter change event
      const event = new CustomEvent('featured-filters:filter-change', {
        detail: { filterName, filterValue, action },
        bubbles: true,
      });
      document.dispatchEvent(event);
    }
  }

  /**
   * Initialize filter button states from URL parameters
   */
  initializeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const activeFilters = {};

    // Parse active filters from URL
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('filter.')) {
        if (!activeFilters[key]) {
          activeFilters[key] = [];
        }
        activeFilters[key].push(value);
      }
    }

    // Update button states to reflect URL state
    this.updateButtonStates(activeFilters);
  }

  /**
   * Update filter button states to reflect current filter state
   * @param {Object} activeFilters - Current active filters from filter drawer
   */
  updateButtonStates(activeFilters = {}) {
    this.options.forEach((checkbox) => {
      const filterName = checkbox.dataset.type;
      const filterValue = checkbox.dataset.value;

      // Check if this filter should be active. Normalize both sides (trim +
      // lowercase) so a button's free-text data-value matches the drawer's canonical
      // active-filter value regardless of case/whitespace differences.
      let shouldBeChecked = false;
      if (activeFilters[filterName] && Array.isArray(activeFilters[filterName])) {
        const normalizedValue = normalizeFilterValue(filterValue);
        shouldBeChecked = activeFilters[filterName].some(
          (activeValue) => normalizeFilterValue(activeValue) === normalizedValue
        );
      }

      // Update checkbox state
      checkbox.checked = shouldBeChecked;

      // Update visual state of the containing button group
      const buttonGroup = checkbox.closest('.filter__buttons');
      if (buttonGroup) {
        const hasAnyChecked = buttonGroup.querySelectorAll('input[type="checkbox"]:checked').length > 0;
        buttonGroup.dataset.active = hasAnyChecked ? 'true' : '';
      }
    });
  }
}

customElements.define('featured-filters', FeaturedFilters);
